import {
  ACTIVITY_IDLE_MS,
  MAX_ACTIVITY_AGE_MS,
  MAX_ACTIVITY_MINUTES,
  minuteStart,
  type ActivityMinute,
  type HumanTimeArea,
  type HumanTimeDevice,
} from '@shared/human-time';

/**
 * People's time (T-102), the browser's half: which minutes this tab counts, held in memory until they're
 * sent. A minute counts when, during it, the tab is visible and focused and the person typed, clicked,
 * scrolled or touched in the last 2 minutes. The minute is kept against the tab's project, open task,
 * page area and device at that moment (the latest sample in the minute wins). The Worker keeps one row per person per
 * minute, so other tabs and devices counting the same minute never add to it.
 *
 * Nothing is stored in the browser: a tab closed before it could send loses at most its last minute.
 * hooks/use-activity-tracking.ts wires it to the page.
 */

/** Keepalive requests share 64 KB in flight; a batch of 120 minutes is about 18 KB. */
const KEEPALIVE_BATCHES = 3;

export interface ActivityContext {
  project_id: string | null;
  task_id: string | null;
  area: HumanTimeArea;
  /** The phone layout or not, at the sample (SF-T-281). */
  device: HumanTimeDevice;
}

export interface ActivityPage {
  now(): number;
  visible(): boolean;
  focused(): boolean;
  /** Where the tab is; null while that isn't known yet (a project page whose project hasn't loaded). */
  context(): ActivityContext | null;
}

/** → whether the Worker took them (a refusal it will never take, like a bad body, counts as taken). */
export type ActivitySender = (minutes: ActivityMinute[], opts: { keepalive: boolean }) => Promise<boolean>;

/** Whether the tab counts right now. */
export function isActive(page: Pick<ActivityPage, 'visible' | 'focused'>, lastInputAt: number, now: number): boolean {
  return page.visible() && page.focused() && now - lastInputAt <= ACTIVITY_IDLE_MS;
}

export class ActivityTracker {
  private lastInputAt = Number.NEGATIVE_INFINITY;
  private readonly pending = new Map<number, ActivityMinute>();
  private sending = false;

  constructor(
    private readonly page: ActivityPage,
    private readonly send: ActivitySender
  ) {}

  /** The person typed, clicked, scrolled or touched. */
  input(): void {
    this.lastInputAt = this.page.now();
    this.sample();
  }

  /** Counts the current minute if the tab is active now. */
  sample(): void {
    const now = this.page.now();
    if (!isActive(this.page, this.lastInputAt, now)) return;
    const context = this.page.context();
    if (!context) return;
    const minute = minuteStart(now);
    // Re-inserted so the map stays oldest first.
    this.pending.delete(minute);
    this.pending.set(minute, { minute: new Date(minute).toISOString(), ...context });
  }

  /** The minutes waiting to be sent, oldest first (tests and the hook). */
  waiting(): ActivityMinute[] {
    return [...this.pending.values()];
  }

  /**
   * Sends the oldest waiting minutes (at most MAX_ACTIVITY_MINUTES), and the next batch while each is
   * taken. Kept for the next try if a send fails; a minute counted again while it was on its way stays to
   * be sent again. `keepalive` when the tab is being hidden or closed: it goes even if one is already on
   * its way, and every batch (up to KEEPALIVE_BATCHES, the browser's keepalive allowance) leaves at once,
   * since a closing page can't wait for answers.
   */
  async flush(keepalive = false): Promise<void> {
    this.dropStale();
    if (!this.pending.size || (this.sending && !keepalive)) return;
    if (keepalive) {
      const all = [...this.pending.entries()];
      const batches = Array.from({ length: Math.min(KEEPALIVE_BATCHES, Math.ceil(all.length / MAX_ACTIVITY_MINUTES)) }, (_, i) =>
        all.slice(i * MAX_ACTIVITY_MINUTES, (i + 1) * MAX_ACTIVITY_MINUTES)
      );
      await Promise.all(batches.map((batch) => this.sendBatch(batch, true)));
      return;
    }
    this.sending = true;
    try {
      while (this.pending.size) {
        const batch = [...this.pending.entries()].slice(0, MAX_ACTIVITY_MINUTES);
        if (!(await this.sendBatch(batch, false)) || batch.length < MAX_ACTIVITY_MINUTES) return;
      }
    } finally {
      this.sending = false;
    }
  }

  /** → whether it was taken; what was taken (and not counted again meanwhile) stops waiting. */
  private async sendBatch(batch: Array<[number, ActivityMinute]>, keepalive: boolean): Promise<boolean> {
    const ok = await this.send(
      batch.map(([, m]) => m),
      { keepalive }
    );
    if (!ok) return false;
    for (const [minute, sent] of batch) {
      if (this.pending.get(minute) === sent) this.pending.delete(minute);
    }
    return true;
  }

  /** Minutes the Worker would refuse as too old. */
  private dropStale(): void {
    const oldest = minuteStart(this.page.now()) - MAX_ACTIVITY_AGE_MS + 60_000;
    for (const minute of this.pending.keys()) {
      if (minute < oldest) this.pending.delete(minute);
      else break;
    }
  }
}
